import { LogOut, HelpCircle, Settings, Info, UserPlus, Edit3, Eye, Camera, X, Shield, Clock } from "lucide-react";
import { useEffect, useState, useRef, useCallback, useContext } from "react";
import { AuthContext } from '../../context/AuthContext';
import Cropper from "react-easy-crop";
import { createPortal } from 'react-dom';
import api from "../../utils/api";
import API_BASE_URL from "../../config";
import HelpScreen from './HelpScreen';
import SettingsScreen from './SettingsScreen';
import AboutScreen from './AboutScreen';
import EditProfileScreen from './EditProfileScreen';
import ReferralScreen from './ReferralScreen';
import EmailLoginSecurityScreen from './EmailLoginSecurityScreen';
import FreezeScreen from './FreezeScreen';


const ProfileSettings = () => {
  const [user, setUser] = useState(null);

  const [editMode, setEditMode] = useState(false);  // full-page view
  const [referralMode, setReferralMode] = useState(false);  // full-page view
  const [freezeMode, setFreezeMode] = useState(false);      // full-page view
  const [isFreezeEnabled, setIsFreezeEnabled] = useState(false);
  const [aboutMode, setAboutMode] = useState(false);  // full-page view
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralsUsed, setReferralsUsed] = useState([]);
  const [gymId, setGymId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [helpMode, setHelpMode] = useState(false);
  const [settingsMode, setSettingsMode] = useState(false);
  const [securityMode, setSecurityMode] = useState(false);
  const [profilePicPopup, setProfilePicPopup] = useState(false);
  const [viewProfilePic, setViewProfilePic] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Cropping state for profile pic upload from popup
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);

  const profileFileRef = useRef(null);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.addEventListener('load', () => resolve(img));
      img.addEventListener('error', (e) => reject(e));
      img.src = url;
    });

  const getCroppedImage = useCallback(async () => {
    try {
      if (!croppedAreaPixels || !imageSrc) return;
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 200;
      canvas.height = 200;
      ctx.drawImage(
        image,
        croppedAreaPixels.x, croppedAreaPixels.y,
        croppedAreaPixels.width, croppedAreaPixels.height,
        0, 0, 200, 200
      );
      const croppedUrl = canvas.toDataURL('image/jpeg', 0.8);
      const resp = await fetch(croppedUrl);
      const blob = await resp.blob();
      const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });

      // Upload immediately
      const formData = new FormData();
      formData.append('FirstName', user.firstname);
      formData.append('LastName', user.lastname);
      formData.append('Gender', user.gender);
      formData.append('Phone', user.phone);
      formData.append('Username', user.email);
      formData.append('Photo', file);

      const res = await api.post('/api/Profile/updateProfile', formData);
      if (res.status === 200) {
        const data = res.data;
        const photoToUse = data.photoUrl || croppedUrl;
        setUser(prev => ({ ...prev, photoUrl: photoToUse }));
        updateUser({ photoUrl: photoToUse }); // always sync — not just when backend URL exists
      }

      setCropOpen(false);
      setImageSrc(null);
    } catch (e) {
      console.error('Error cropping image', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedAreaPixels, imageSrc, user]);

  const handleProfileFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setImageSrc(url);
      setCropOpen(true);
      setProfilePicPopup(false);
    }
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const { user: authUser, logout, updateUser } = useContext(AuthContext);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  const fetchUserProfile = async () => {
    try {
      const userinfo = authUser;
      if (!userinfo?.username) return;

      setUserId(userinfo.id);

      const activeGymcode = localStorage.getItem('fitscan_active_gymcode') || userinfo.gymcode;
      const response = await api.post('/api/profile/userprofile', {
        Username: userinfo.username,
        Gymcode: activeGymcode
      });
      const data = response.data;

      setUser((prev) => ({
        ...prev,
        name: data.fullName || "",
        firstname: data.firstName || "",
        lastname: data.lastName || "",
        email: data.email || "",
        phone: data.phoneNumber || "",
        photoUrl: data.photoUrl || "",
        gender: data.gender || "",
        dateOfBirth: data.dateOfBirth || "",
        totalVisits: data.totalvisits || "",
        planExpiresInDays: data.expiresIn || "",
        planExpirydate: data.userExpiryDateShort || "",
        joiningDate: data.userJoiningDateShort || "",
      }));

      // Sync photo to AuthContext so top-right avatar updates
      const resolvedPhoto = data.photoUrl || "";
      if (resolvedPhoto) {
        updateUser({ photoUrl: resolvedPhoto });
      }

      setGymId(data.gymId || null);
    } catch (err) {
      console.error('userprofile error:', err.response?.data || err.message);
    }
  };

  const fetchFreezeSettings = async () => {
    try {
      const activeGymcode = localStorage.getItem('fitscan_active_gymcode') || authUser?.gymcode;
      if (!activeGymcode) return;
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const res = await fetch(`${API_BASE_URL}/api/membershipfreeze/settings?gymCode=${activeGymcode}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsFreezeEnabled(data.isFreezeEnabled);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchReferralInfo = async () => {
    try {
      const Username = authUser?.username;
      const Gymcode = authUser?.gymcode;
      if (!Username || !Gymcode) return;

      const res = await api.post('/api/Referral/user/referral', {
        username: Username,
        gymcode: Gymcode,
      });

      let referral = res.data;

      if (!referral) {
        const createRes = await api.post('/api/Referral/create', {
          username: Username,
          gymcode: Gymcode,
        });
        referral = createRes.data;
      }
      setReferralInfo(referral);
      setReferralsUsed(referral.referralStatuses || []);
    } catch (err) {
      console.error("Error fetching referral info:", err);
    }
  };

  const handleEditClick = () => {
    setEditMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSecurityClick = () => {
    setSecurityMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReferralClick = async () => {
    if (!referralInfo && userId && gymId) {
      await fetchReferralInfo();
    }
    setReferralMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAboutClick = () => {
    setAboutMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleHelpClick = () => {
    setHelpMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSettingsClick = () => {
    setSettingsMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!authUser?.username) return;
    Promise.all([fetchUserProfile(), fetchReferralInfo(), fetchFreezeSettings()]).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.username]);

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6CD800]" />
        </div>
      ) : editMode ? (
        <EditProfileScreen onBack={() => setEditMode(false)} user={user} setUser={setUser} />
      ) : securityMode ? (
        <EmailLoginSecurityScreen onBack={() => setSecurityMode(false)} user={user} />
      ) : referralMode ? (
        <ReferralScreen onBack={() => setReferralMode(false)} referralInfo={referralInfo} referralsUsed={referralsUsed} />
      ) : freezeMode ? (
        <FreezeScreen onBack={() => setFreezeMode(false)} gymCode={localStorage.getItem('fitscan_active_gymcode') || authUser?.gymcode} />
      ) : helpMode ? (
        <HelpScreen onBack={() => setHelpMode(false)} />
      ) : settingsMode ? (
        <SettingsScreen onBack={() => setSettingsMode(false)} />
      ) : aboutMode ? (
        <AboutScreen onBack={() => setAboutMode(false)} />
      ) : (
        <div className="section-padding pt-0 pb-6 space-y-6 mt-0">
          <div className="flex flex-col items-center gap-3 text-white">
            <div
              className="w-24 h-24 rounded-full bg-[#6CD800] text-[#181f10] flex items-center justify-center text-4xl font-bold overflow-hidden cursor-pointer relative group"
              onClick={() => setProfilePicPopup(true)}
            >
              {user.photoUrl ? (
                <img
                  src={user.photoUrl.replace(/\\/g, '/')}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                user.firstname?.charAt(0) || '?'
              )}
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera size={20} className="text-white" />
              </div>
            </div>
            <h2 className="text-xl font-semibold">{user.name || "User Name"}</h2>
          </div>

          <div className="bg-[#222] border border-[#2b2b2b] rounded-xl p-4 space-y-2 text-white text-sm">
            <div className="flex justify-between">
              <span>Total Visits</span>
              <span className="text-[#6CD800]">{user.totalVisits}</span>
            </div>
            <div className="flex justify-between">
              <span>Date of Joining</span>
              <span className="text-[#6CD800]">{user.joiningDate}</span>
            </div>
            <div className="flex justify-between">
              <span>Plan Expires In</span>
              <span className="text-yellow-400">{user.planExpiresInDays} days</span>
            </div>
            <div className="flex justify-between">
              <span>Plan Expiry Date</span>
              <span className="text-yellow-400">{user.planExpirydate}</span>
            </div>
          </div>

          <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl divide-y divide-[#2b2b2b]">
            {[
              { icon: <Edit3 size={18} />, label: "Edit Your Profile", onClick: handleEditClick },
              { icon: <Shield size={18} />, label: "Email & Login Security", onClick: handleSecurityClick },
              { icon: <UserPlus size={18} />, label: "Referral", onClick: handleReferralClick },
              ...(isFreezeEnabled ? [{ icon: <Clock size={18} />, label: "Membership Freeze", onClick: () => setFreezeMode(true) }] : []),
              { icon: <HelpCircle size={18} />, label: "Help", onClick: handleHelpClick },
              { icon: <Info size={18} />, label: "About", onClick: handleAboutClick },
              { icon: <Settings size={18} />, label: "Settings", onClick: handleSettingsClick }
            ].map((item, idx) => (
              <button key={idx} onClick={item.onClick} className="flex items-center justify-between w-full px-4 py-3 hover:bg-[#222] text-gray-300 transition">
                <div className="flex items-center gap-3">
                  <span className="text-[#6CD800]">{item.icon}</span>
                  {item.label}
                </div>
                <span className="text-gray-500">›</span>
              </button>
            ))}
          </div>

          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 text-red-500 border border-red-500 hover:bg-red-500 hover:text-white transition py-2 rounded-xl font-semibold">
            <LogOut size={18} />
            Log out
          </button>
        </div>
      )}

      {/* Hidden file input for profile pic upload */}
      <input
        type="file"
        accept="image/*"
        ref={profileFileRef}
        onChange={handleProfileFileChange}
        style={{ display: 'none' }}
      />

      {/* Profile Pic Popup (Bottom Sheet) */}
      {profilePicPopup && createPortal(
        <div
          className="fixed inset-0 z-[998] flex items-end justify-center"
          onClick={() => setProfilePicPopup(false)}
          style={{ background: 'rgba(0,0,0,0.6)' }}
        >
          <div
            className="w-full max-w-[480px] rounded-t-2xl p-5 pb-8"
            style={{
              background: '#181818',
              border: '0.5px solid #2b2b2b',
              animation: 'slideUpPopup 0.25s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <span className="text-white font-medium text-base">Profile Photo</span>
              <button
                onClick={() => setProfilePicPopup(false)}
                className="text-gray-500 hover:text-white transition p-1 rounded-full hover:bg-white/10"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-1">
              <button
                onClick={() => {
                  setProfilePicPopup(false);
                  if (user.photoUrl) {
                    setViewProfilePic(true);
                  } else {
                    alert('No profile picture set yet.');
                  }
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#222] text-gray-300 transition text-left"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(108,216,0,0.12)' }}>
                  <Eye size={18} className="text-[#6CD800]" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">See profile picture</div>
                  <div className="text-xs text-gray-500">View your current photo</div>
                </div>
              </button>

              <button
                onClick={() => {
                  setProfilePicPopup(false);
                  profileFileRef.current?.click();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#222] text-gray-300 transition text-left"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.12)' }}>
                  <Camera size={18} className="text-blue-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">Choose profile picture</div>
                  <div className="text-xs text-gray-500">Upload a new photo</div>
                </div>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Full-Screen Profile Picture Viewer */}
      {viewProfilePic && user.photoUrl && createPortal(
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.9)' }}
          onClick={() => setViewProfilePic(false)}
        >
          <button
            onClick={() => setViewProfilePic(false)}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition p-2 rounded-full hover:bg-white/10 z-10"
          >
            <X size={24} />
          </button>
          <img
            src={user.photoUrl.replace(/\\/g, '/')}
            alt="Profile"
            className="max-w-[90vw] max-h-[80vh] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}

      {/* Cropping Modal for profile pic from popup */}
      {cropOpen && imageSrc && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1000] p-4">
          <div className="bg-[#222] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="relative h-96 w-full mb-4 bg-gray-800 rounded-lg">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropDoubleClick={() => setZoom(1)}
                onZoomChange={setZoom}
                onMediaLoaded={() => setCroppedAreaPixels(null)}
                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setCropOpen(false);
                  setImageSrc(null);
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={getCroppedImage}
                className="px-6 py-2 bg-[#6CD800] hover:bg-[#6CD800]/90 text-[#181f10] rounded-lg font-medium disabled:opacity-50"
                disabled={!croppedAreaPixels}
              >
                Crop & Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideUpPopup {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ProfileSettings;
